import { useEffect, type RefObject } from "react";

const VIMEO_ORIGIN = "https://player.vimeo.com";

interface VimeoMessage {
  event?: string;
  method?: string;
  data?: { seconds?: number };
}

export function vimeoEmbedUrl(videoId: string, startAt = 0) {
  const params = new URLSearchParams({
    autoplay: "1",
    title: "0",
    byline: "0",
    portrait: "0",
    dnt: "1",
  });
  const fragment = startAt > 1 ? `#t=${Math.floor(startAt)}s` : "";
  return `${VIMEO_ORIGIN}/video/${videoId}?${params}${fragment}`;
}

/**
 * Suit la position de lecture d'un lecteur Vimeo embarqué via son API
 * postMessage, pour pouvoir reprendre la vidéo au même endroit quand le
 * lecteur change de conteneur (modale ↔ mini-lecteur).
 */
export default function useVimeoPlayer(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  onTimeUpdate: (seconds: number) => void,
) {
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const iframe = iframeRef.current;
      if (
        event.origin !== VIMEO_ORIGIN ||
        !iframe ||
        event.source !== iframe.contentWindow
      ) {
        return;
      }
      let message: VimeoMessage;
      try {
        message =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (message.event === "ready") {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ method: "addEventListener", value: "timeupdate" }),
          VIMEO_ORIGIN,
        );
      } else if (
        message.event === "timeupdate" &&
        typeof message.data?.seconds === "number"
      ) {
        onTimeUpdate(message.data.seconds);
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [iframeRef, onTimeUpdate]);
}
